import { useState, useEffect, useCallback } from 'react';
import { Modal, Box, Text, Link, Icon } from '@nimbus-ds/components';
import { ExternalLinkIcon } from '@nimbus-ds/icons';
import { InteractiveListPriv } from '@tiendanube/components';
import { FEATURE_ABANDONED_CARTS_AUTOMATED_EMAILS } from 'App/features';
import { CancelAndSaveButtons } from 'commons/components';
import { useAsyncFunc, useToast } from 'commons/hooks';
import goToAdmin from 'commons/utils/gotToAdmin';
import { ORDERS_ABANDONED_CARTS_AUTOMATED_EMAILS_CONFIG } from 'config/upsellFlowSources';
import { useIsMobileDevice } from 'domains/Auth/hooks';
import useUpsellFlow from 'domains/Billing/UpsellFlow/hooks/useUpsellFlow';
import { AbandonedCartEmalConfg } from 'domains/Orders/AbandonedCart/abandonedCartsSlice';
import { useAbandonedCartConfigEmail } from 'domains/Orders/AbandonedCart/hooks';
import useTranslationOrders from 'domains/Orders/useTranslationOrders';

interface ConfigEmailModalProps {
  isOpen: boolean;
  onClose: () => void;
}

function AbandonedCartsConfigEmailModal({
  isOpen,
  onClose,
}: ConfigEmailModalProps) {
  const t = useTranslationOrders();
  const { addToast } = useToast();
  const isMobileDevice = useIsMobileDevice();
  const { abandonedCartEmailConfig, updateEmailConfig, isLoading } =
    useAbandonedCartConfigEmail();
  const [handleOnSave] = useAsyncFunc(
    async () => {
      await updateEmailConfig(config);
      onClose();
    },
    () =>
      addToast({
        label: t('abandonedCarts.emailConfigModal.toast.success'),
        appearance: 'success',
      }),
    () =>
      addToast({
        label: t('abandonedCarts.emailConfigModal.toast.error'),
        appearance: 'danger',
      }),
  );

  const [config, setConfig] = useState<AbandonedCartEmalConfg>(
    abandonedCartEmailConfig,
  );

  const handleConfigChange = (configValue: AbandonedCartEmalConfg) => {
    setConfig(configValue);
  };

  const triggerUpsellFlow = useUpsellFlow({
    featureKey: FEATURE_ABANDONED_CARTS_AUTOMATED_EMAILS,
    trackingSource: ORDERS_ABANDONED_CARTS_AUTOMATED_EMAILS_CONFIG,
    callback: (changeEvent: { name: string }) => {
      handleConfigChange(changeEvent.name as AbandonedCartEmalConfg);
    },
  });

  const handleOnSelect = useCallback(
    (changeEvent: { name: string }) => {
      const isSelectingAutomaticOption = changeEvent.name === 'automatic';
      if (isSelectingAutomaticOption) {
        triggerUpsellFlow(changeEvent);
      } else {
        handleConfigChange(changeEvent.name as AbandonedCartEmalConfg);
      }
    },
    [triggerUpsellFlow],
  );

  const handleOnClose = () => {
    onClose();
    setConfig(abandonedCartEmailConfig);
  };

  useEffect(() => {
    setConfig(abandonedCartEmailConfig);
  }, [abandonedCartEmailConfig]);

  return (
    <Modal open={isOpen} onDismiss={handleOnClose}>
      <Modal.Header title={t('abandonedCarts.emailConfigModal.title')} />
      <Modal.Body padding="none">
        <Box display="flex" flexDirection="column" gap="4">
          <Text>{t('abandonedCarts.emailConfigModal.description')}</Text>
          <Box display="flex" flexDirection="column" gap="2">
            <InteractiveListPriv>
              <InteractiveListPriv.RadioItem
                title={t('abandonedCarts.emailConfigModal.automatic.label')}
                checked={config === 'automatic'}
                onChange={handleOnSelect}
                value="automatic"
                description={t(
                  'abandonedCarts.emailConfigModal.automatic.description',
                )}
              />
              <InteractiveListPriv.RadioItem
                title={t('abandonedCarts.emailConfigModal.manual.label')}
                checked={config === 'manual'}
                onChange={handleOnSelect}
                value="manual"
                description={t(
                  'abandonedCarts.emailConfigModal.manual.description',
                )}
              />
            </InteractiveListPriv>
          </Box>

          {!isMobileDevice && (
            <Link
              as="button"
              onClick={goToAdmin('notifications')}
              appearance="primary"
              textDecoration="none"
            >
              {t('abandonedCarts.emailConfigModal.link')}
              <Icon source={<ExternalLinkIcon />} color="currentColor" />
            </Link>
          )}
        </Box>
      </Modal.Body>
      <Modal.Footer>
        <CancelAndSaveButtons
          onCancel={handleOnClose}
          onSave={handleOnSave}
          isLoading={isLoading}
          isDisabled={isLoading}
        />
      </Modal.Footer>
    </Modal>
  );
}

export default AbandonedCartsConfigEmailModal;
